import { useLayoutEffect, useRef } from 'react';

// Shrink-to-fit sizing for a Pup card's text boxes (see PupCard.js).
//
// The card template is a FIXED 240x330 layout — every render context (the dock, the felt
// overlay, the staging/auto-play boxes, a permanent parked on the felt, the detail modal)
// only ever scales it with a CSS `transform`, which does not touch layout. So a box that
// overflows at one scale overflows at every scale, and one measurement per content change
// is enough for all of them.
//
// The text itself is server-owned (the GET /cards/catalog copy), so a long name, modifier or
// description would otherwise spill out of its box. This hook treats the font-size declared
// in PupCard.css as the MAXIMUM and steps it down until the content fits; a card whose text
// already fits is left completely alone.
//
//   axis 'width'  — single-line boxes (Name, Modifier). The element must be `white-space:
//                   nowrap; overflow: hidden` with a bounded width, so scrollWidth >
//                   clientWidth is exactly "this needs more than one line's worth of room".
//   axis 'height' — block boxes (Description). The element must be height-bounded (a flex
//                   child with `min-height: 0`) and `overflow: hidden`, so scrollHeight >
//                   clientHeight means the wrapped text is taller than its box.
//
// Measurement uses scrollWidth/clientWidth — LAYOUT metrics, unaffected by an ancestor's
// transform — never getBoundingClientRect, so every scaled context agrees on the answer.
// Under jsdom every metric reads 0, so nothing overflows and the CSS size stands.

const STEP_PX = 0.5;      // final precision of the fitted size
const OVERFLOW_SLACK = 1; // scroll/client metrics are rounded ints; ignore 1px of noise

// `contentKey` must change whenever the rendered text does (card code + the text itself, so a
// mid-session catalog patch re-fits too). Returns the ref to put on the element.
export default function useFitText(contentKey, axis = 'width', minPx = 8) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const overflows = () => (axis === 'height'
      ? el.scrollHeight - el.clientHeight > OVERFLOW_SLACK
      : el.scrollWidth - el.clientWidth > OVERFLOW_SLACK);

    const fit = () => {
      el.style.fontSize = ''; // back to the CSS-declared maximum before measuring
      const max = parseFloat(window.getComputedStyle(el).fontSize);
      if (!(max > minPx) || !overflows()) return;

      // Bisect for the largest fitting size instead of walking down in steps: ~5 forced
      // reflows per box rather than ~30, which matters when a whole dock mounts at once.
      // Invariant: `lo` is assumed to fit, `hi` is known to overflow.
      let lo = minPx;
      let hi = max;
      while (hi - lo > STEP_PX) {
        const mid = Math.round(((lo + hi) / 2) / STEP_PX) * STEP_PX;
        if (mid <= lo || mid >= hi) break; // snapping collapsed the interval — done
        el.style.fontSize = `${mid}px`;
        if (overflows()) hi = mid; else lo = mid;
      }
      // If even `minPx` overflows we settle there and `overflow: hidden` clips the tail —
      // no catalog card reaches that today (the worst description lands a few px above it).
      el.style.fontSize = `${lo}px`;
    };

    fit();

    // Font metrics can land after the first paint on a cold cache, which would invalidate
    // the measurement above. Re-fit once the document's fonts settle; jsdom has no
    // FontFaceSet, hence the guard.
    let cancelled = false;
    const fonts = typeof document !== 'undefined' ? document.fonts : null;
    if (fonts && typeof fonts.ready?.then === 'function') {
      fonts.ready.then(() => {
        if (!cancelled && ref.current === el) fit();
      }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [contentKey, axis, minPx]);

  return ref;
}
